/**
 * EligibilityRuleBuilder — per-question qualification rules (WCR parity).
 * A red "NOT ELIGIBLE if…" rule (exclude_if) and an amber "flag for REVIEW
 * if…" rule (flag_for_review_if), each an operator + value. Operators and the
 * value widget adapt to the question type; numeric thresholds are stored as
 * strings (the WCR shape — the server Number()-coerces at evaluation).
 */
import { X, Plus } from 'lucide-react';
import { FormEligibilityRule } from '../../types';
import { OPERATOR_LABELS, eligibilityOperatorsForType } from './formMeta';

type Rule = { operator: string; value: any };

interface EligibilityRuleBuilderProps {
  rule: FormEligibilityRule | null | undefined;
  questionType: string;
  options: { value: string; label: string }[];
  onChange: (rule: FormEligibilityRule | null) => void;
}

const isBetween = (op: string) => op === 'between' || op === 'not_between';

/** A fresh value matching the operator's expected shape. */
const defaultValue = (op: string): any => (isBetween(op) ? ['', ''] : op === 'in' ? [] : '');

const COLORS = {
  exclude: {
    box: 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20',
    title: 'text-red-700 dark:text-red-300',
    select: 'border-red-300 focus:border-red-500 focus:ring-red-500/20 dark:border-red-700',
    close: 'text-red-600 hover:bg-red-100 dark:text-red-300 dark:hover:bg-red-900/40',
    add: 'border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20',
    heading: 'Mark as NOT ELIGIBLE if answer',
    addLabel: 'Add exclusion rule',
  },
  flag: {
    box: 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20',
    title: 'text-amber-700 dark:text-amber-300',
    select: 'border-amber-300 focus:border-amber-500 focus:ring-amber-500/20 dark:border-amber-700',
    close: 'text-amber-600 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/40',
    add: 'border-amber-200 text-amber-600 hover:bg-amber-50 dark:border-amber-800 dark:text-amber-400 dark:hover:bg-amber-900/20',
    heading: 'Flag for REVIEW if answer',
    addLabel: 'Add review flag rule',
  },
};

function RuleRow({
  kind, rule, questionType, options, onChange, onRemove,
}: {
  kind: 'exclude' | 'flag';
  rule: Rule;
  questionType: string;
  options: { value: string; label: string }[];
  onChange: (r: Rule) => void;
  onRemove: () => void;
}) {
  const c = COLORS[kind];
  const ops = eligibilityOperatorsForType(questionType);
  const inputCls = `block w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 dark:bg-gray-800 dark:text-gray-100 ${c.select}`;

  const setOperator = (op: string) => {
    // Reset the value shape when crossing the between/non-between or in boundary.
    const wasArray = isBetween(rule.operator) || rule.operator === 'in';
    const nowArray = isBetween(op) || op === 'in';
    onChange({ operator: op, value: wasArray === nowArray ? rule.value : defaultValue(op) });
  };

  const renderValue = () => {
    const op = rule.operator;
    if (options.length > 0 && ['equals', 'not_equals', 'contains'].includes(op)) {
      return (
        <select value={String(rule.value ?? '')} onChange={(e) => onChange({ operator: op, value: e.target.value })} className={inputCls}>
          <option value="">Select a value</option>
          {options.map((o, i) => <option key={`${i}:${o.value}`} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    if (questionType === 'yes_no' && ['equals', 'not_equals'].includes(op)) {
      return (
        <select value={String(rule.value ?? '')} onChange={(e) => onChange({ operator: op, value: e.target.value })} className={inputCls}>
          <option value="">Select a value</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      );
    }
    if (isBetween(op)) {
      const range: any[] = Array.isArray(rule.value) ? rule.value : ['', ''];
      return (
        <div className="flex items-center gap-2">
          <input type="number" value={range[0] ?? ''} onChange={(e) => onChange({ operator: op, value: [e.target.value, range[1] ?? ''] })} placeholder="Min" className={inputCls} />
          <span className="text-sm text-gray-500 dark:text-gray-400">and</span>
          <input type="number" value={range[1] ?? ''} onChange={(e) => onChange({ operator: op, value: [range[0] ?? '', e.target.value] })} placeholder="Max" className={inputCls} />
        </div>
      );
    }
    if (op === 'in' && options.length > 0) {
      // Pick by label, store the stable option VALUEs — a free-text list would
      // store labels that can never match the opaque option_N answer values.
      const sel: string[] = Array.isArray(rule.value) ? rule.value.map(String) : [];
      const toggle = (v: string) => {
        const next = sel.includes(v) ? sel.filter((x) => x !== v) : [...sel, v];
        onChange({ operator: op, value: next });
      };
      return (
        <div className="flex flex-wrap gap-1.5">
          {options.map((o, i) => (
            <button
              key={`${i}:${o.value}`}
              type="button"
              onClick={() => toggle(o.value)}
              className={`rounded-lg border px-3 py-1.5 text-sm transition-colors duration-150 ${
                sel.includes(o.value)
                  ? 'border-primary-600 bg-primary-50 text-primary-800 dark:border-primary-500 dark:bg-primary-900/30 dark:text-primary-200'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      );
    }
    if (op === 'in') {
      return (
        <input
          type="text"
          value={Array.isArray(rule.value) ? rule.value.join(', ') : ''}
          onChange={(e) => onChange({ operator: op, value: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
          placeholder="value1, value2"
          className={inputCls}
        />
      );
    }
    if (['number', 'scale', 'calculation'].includes(questionType) || ['greater_than', 'less_than'].includes(op)) {
      return <input type="number" value={String(rule.value ?? '')} onChange={(e) => onChange({ operator: op, value: e.target.value })} placeholder="Enter a number" className={inputCls} />;
    }
    return <input type="text" value={String(rule.value ?? '')} onChange={(e) => onChange({ operator: op, value: e.target.value })} placeholder="Enter a value" className={inputCls} />;
  };

  return (
    <div className={`rounded-lg border p-4 ${c.box}`}>
      <div className="mb-3 flex items-center justify-between">
        <span className={`text-sm font-medium ${c.title}`}>{c.heading}</span>
        <button type="button" onClick={onRemove} className={`rounded p-1 ${c.close}`} aria-label="Remove rule">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select value={rule.operator} onChange={(e) => setOperator(e.target.value)} className={`${inputCls} w-auto`}>
          {ops.map((op) => <option key={op} value={op}>{OPERATOR_LABELS[op]}</option>)}
        </select>
        <div className="min-w-[150px] flex-1">{renderValue()}</div>
      </div>
    </div>
  );
}

export default function EligibilityRuleBuilder({ rule, questionType, options, onChange }: EligibilityRuleBuilderProps) {
  const exclude = rule?.exclude_if || null;
  const flag = rule?.flag_for_review_if || null;
  const firstOp = eligibilityOperatorsForType(questionType)[0] || 'equals';

  const patch = (next: Partial<FormEligibilityRule>) => {
    const merged: FormEligibilityRule = {
      exclude_if: 'exclude_if' in next ? next.exclude_if : exclude,
      flag_for_review_if: 'flag_for_review_if' in next ? next.flag_for_review_if : flag,
    };
    onChange(merged.exclude_if || merged.flag_for_review_if ? merged : null);
  };

  return (
    <div className="space-y-3">
      {exclude ? (
        <RuleRow
          kind="exclude" rule={exclude} questionType={questionType} options={options}
          onChange={(r) => patch({ exclude_if: r })}
          onRemove={() => patch({ exclude_if: null })}
        />
      ) : (
        <button type="button" onClick={() => patch({ exclude_if: { operator: firstOp, value: defaultValue(firstOp) } })} className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors duration-150 ${COLORS.exclude.add}`}>
          <Plus className="h-4 w-4" /> {COLORS.exclude.addLabel}
        </button>
      )}

      {flag ? (
        <RuleRow
          kind="flag" rule={flag} questionType={questionType} options={options}
          onChange={(r) => patch({ flag_for_review_if: r })}
          onRemove={() => patch({ flag_for_review_if: null })}
        />
      ) : (
        <button type="button" onClick={() => patch({ flag_for_review_if: { operator: firstOp, value: defaultValue(firstOp) } })} className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors duration-150 ${COLORS.flag.add}`}>
          <Plus className="h-4 w-4" /> {COLORS.flag.addLabel}
        </button>
      )}
    </div>
  );
}
